<template>
  <!-- App-wide overlay: a floating "flag" button + capture panel that sits on
       top of the entire SPA. Lets an operator screenshot whatever they're
       looking at and file it as a Focus Item (Requirement type 40), attaching
       the screenshot as the visual record. The MEANING is authored by the
       operator — we never interpret the image. -->
  <div v-if="canUse" class="fic-root">
    <!-- Floating trigger -->
    <button
      v-if="!panelOpen && !selecting"
      class="fic-fab"
      title="Flag as Focus Item"
      @click="startSelection"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
        <line x1="4" y1="22" x2="4" y2="15"/>
      </svg>
    </button>

    <!-- Region-select overlay: dim the screen, drag a rectangle over the
         thing to flag. Mouse-up captures + crops to that rectangle. -->
    <div
      v-if="selecting"
      class="fic-select-layer"
      @mousedown="onSelectStart"
    >
      <div class="fic-select-hint">Drag a box around what you want to flag · Esc to cancel</div>
      <div v-if="hasSelection" class="fic-select-rect" :style="selectRectStyle" />
    </div>

    <!-- Capture / create modal (centered, backdrop) -->
    <div v-if="panelOpen" class="fic-backdrop" @click.self="closePanel">
      <div class="fic-modal">
        <div class="fic-panel-head">
          <span class="fic-panel-title">New Focus Item</span>
          <button class="fic-x" title="Cancel" @click="closePanel">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div class="fic-body">
          <!-- Left: screenshot preview -->
          <div class="fic-shot-col">
            <div class="fic-shot">
              <div v-if="capturing" class="fic-shot-loading">
                <span class="fic-spinner" />
                <span>Capturing…</span>
              </div>
              <img v-else-if="shotUrl" :src="shotUrl" class="fic-shot-img" alt="Captured screenshot" />
              <div v-else class="fic-shot-empty">No screenshot</div>
            </div>
            <button v-if="shotUrl && !capturing" class="fic-recapture" @click="recapture">Re-select region</button>
          </div>

          <!-- Right: form -->
          <div class="fic-form-col">
            <label class="fic-label">Title</label>
            <input
              ref="titleInput"
              v-model="form.title"
              placeholder="What should operators focus on?"
              class="fic-input"
            />

            <label class="fic-label">Description</label>
            <textarea
              v-model="form.description"
              placeholder="Add detail or context"
              rows="6"
              class="fic-textarea"
            />

            <div class="fic-row">
              <div class="fic-field">
                <label class="fic-label">Priority</label>
                <select v-model="form.priority" class="fic-select">
                  <option value="p0">Critical</option>
                  <option value="p1">High</option>
                  <option value="p2">Medium</option>
                  <option value="p3">Low</option>
                </select>
              </div>
              <div v-if="entityOptions.length" class="fic-field fic-field--grow">
                <label class="fic-label">Entity</label>
                <select v-model="form.entity" class="fic-select">
                  <option :value="null">All (ACO-wide)</option>
                  <option v-for="e in entityOptions" :key="e.id" :value="e.id">{{ e.name }}</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div v-if="error" class="fic-error">{{ error }}</div>

        <div class="fic-actions">
          <button class="fic-btn-ghost" @click="closePanel">Cancel</button>
          <button
            class="fic-btn-primary"
            :disabled="!form.title.trim() || saving || capturing"
            @click="save"
          >{{ saving ? 'Saving…' : 'Create Focus Item' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { captureElementCanvas, cropCanvasToBlob, uploadFocusScreenshot } from '@/utils/focusItemCapture'

const FOCUS_ITEM_TYPE_ID = 40

export default {
  name: 'FocusItemCapture',

  emits: ['created'],

  data() {
    return {
      panelOpen: false,
      selecting: false,
      dragging: false,
      // Drag rectangle in viewport (client) px.
      sel: { x0: 0, y0: 0, x1: 0, y1: 0 },
      capturing: false,
      saving: false,
      error: null,
      shotBlob: null,
      shotUrl: null,
      form: { title: '', description: '', priority: 'p1', entity: null },
      entityOptions: [],
      currentEntityName: null,
    }
  },

  computed: {
    ...mapGetters('auth', ['current_user']),
    ...mapGetters('tenant', ['isTenantAdmin']),
    ...mapGetters('entity', ['current_entity_id']),

    // Only show on the External portal (its routes carry layout: 'external'),
    // where an entity is always in scope so a Focus Item can be meaningfully
    // scoped. Admins only (server-side create is role-gated too).
    canUse() {
      const isExternal = this.$route?.meta?.layout === 'external'
      return isExternal && !!(this.current_user?.is_superuser || this.isTenantAdmin)
    },

    hasSelection() {
      return this.selRectClient.width > 2 && this.selRectClient.height > 2
    },

    // The drag rectangle normalized (x0/y0 need not be the top-left).
    selRectClient() {
      const x = Math.min(this.sel.x0, this.sel.x1)
      const y = Math.min(this.sel.y0, this.sel.y1)
      return {
        x, y,
        width: Math.abs(this.sel.x1 - this.sel.x0),
        height: Math.abs(this.sel.y1 - this.sel.y0),
      }
    },

    selectRectStyle() {
      const r = this.selRectClient
      return {
        left: r.x + 'px', top: r.y + 'px',
        width: r.width + 'px', height: r.height + 'px',
      }
    },
  },

  watch: {
    // If we navigate off the External portal mid-flow, tear everything down so
    // no selection overlay / window listeners linger.
    canUse(nowUsable) {
      if (!nowUsable) {
        this.cancelSelection()
        this.closePanel()
      }
    },
  },

  beforeUnmount() {
    if (this.shotUrl) URL.revokeObjectURL(this.shotUrl)
    window.removeEventListener('keydown', this.onSelectKey)
    window.removeEventListener('mousemove', this.onSelectMove)
    window.removeEventListener('mouseup', this.onSelectEnd)
  },

  methods: {
    // Enter region-select mode: dim the screen and wait for a drag.
    startSelection() {
      this.sel = { x0: 0, y0: 0, x1: 0, y1: 0 }
      this.selecting = true
      window.addEventListener('keydown', this.onSelectKey)
    },

    onSelectKey(e) {
      if (e.key === 'Escape') this.cancelSelection()
    },

    cancelSelection() {
      this.selecting = false
      this.dragging = false
      window.removeEventListener('keydown', this.onSelectKey)
      window.removeEventListener('mousemove', this.onSelectMove)
      window.removeEventListener('mouseup', this.onSelectEnd)
    },

    onSelectStart(e) {
      e.preventDefault()
      this.dragging = true
      this.sel = { x0: e.clientX, y0: e.clientY, x1: e.clientX, y1: e.clientY }
      // Track move/up on WINDOW, not the layer: a fast drag can outrun the
      // element and the mouseup then lands off-layer and is lost. Window
      // listeners catch the release wherever it happens.
      window.addEventListener('mousemove', this.onSelectMove)
      window.addEventListener('mouseup', this.onSelectEnd)
    },

    onSelectMove(e) {
      if (!this.dragging) return
      this.sel.x1 = e.clientX
      this.sel.y1 = e.clientY
    },

    async onSelectEnd() {
      if (!this.dragging) return
      this.dragging = false
      window.removeEventListener('mousemove', this.onSelectMove)
      window.removeEventListener('mouseup', this.onSelectEnd)
      if (!this.hasSelection) { this.cancelSelection(); return }
      const rectClient = { ...this.selRectClient }
      // Leave select mode (removes the dim layer) BEFORE capturing so it isn't
      // in the shot; then open the panel and rasterize+crop.
      this.cancelSelection()
      this.resetForm()
      this.form.entity = this.current_entity_id || null
      this.capturing = true      // show the spinner the instant the modal opens
      this.panelOpen = true
      this.fetchEntities()
      await this.$nextTick()
      if (this.$refs.titleInput) this.$refs.titleInput.focus()
      this.captureRegion(rectClient)
    },

    // Rasterize the content region, then crop to the dragged rectangle.
    // Coordinates: the drag rect is in viewport px; convert to coords relative
    // to the captured element's rendered box (its on-screen top-left).
    // Resolve after the browser has committed a paint (two RAFs). Lets the
    // "Capturing…" spinner actually render before html-to-image locks the
    // main thread rasterizing.
    nextPaint() {
      return new Promise(resolve => {
        requestAnimationFrame(() => requestAnimationFrame(resolve))
      })
    },

    async captureRegion(rectClient) {
      this.capturing = true
      this.error = null
      const SCALE = 2
      try {
        const target = document.querySelector('.fic-capture-target') || document.body
        const targetBox = target.getBoundingClientRect()
        // Let the spinner paint before the (blocking) rasterize.
        await this.nextPaint()
        const canvas = await captureElementCanvas(target, {
          scale: SCALE,
          ignore: (el) => !!(el.classList && el.classList.contains('fic-root')),
        })
        // html-to-image renders the target's FULL content box (row 0 = content
        // top), so if the target is a scrolled overflow container we must add
        // its scroll offset: viewport coord -> content coord.
        const rectInEl = {
          x: rectClient.x - targetBox.left + (target.scrollLeft || 0),
          y: rectClient.y - targetBox.top + (target.scrollTop || 0),
          width: rectClient.width,
          height: rectClient.height,
        }
        const blob = await cropCanvasToBlob(canvas, rectInEl, SCALE)
        if (!blob) throw new Error('Empty crop region')
        this.setBlob(blob)
      } catch (e) {
        console.error('Focus item capture failed:', e)
        this.error = 'Could not capture that region.'
      } finally {
        this.capturing = false
      }
    },

    // Re-run the region select from inside the panel.
    recapture() {
      this.panelOpen = false
      this.setBlob(null)
      this.startSelection()
    },

    setBlob(blob) {
      if (this.shotUrl) URL.revokeObjectURL(this.shotUrl)
      this.shotBlob = blob
      this.shotUrl = blob ? URL.createObjectURL(blob) : null
    },

    async fetchEntities() {
      if (!this.current_entity_id) {
        this.entityOptions = []
        return
      }
      try {
        // Current entity + its descendants — same shape the Focus Items widget
        // offers, so an admin can attach to the exact facility/TIN/practice.
        const [cur, kids] = await Promise.all([
          this.$axios.get(`/api/entities/${this.current_entity_id}/`),
          this.$axios.get('/api/entities/', {
            params: { ancestor_ids: this.current_entity_id, page_size: 2000 },
          }),
        ])
        const curE = cur.data || {}
        this.currentEntityName = curE.name || null
        const list = (kids.data?.results || kids.data || [])
          .map(e => ({ id: e.id, name: e.name }))
        const opts = []
        if (curE.id) opts.push({ id: curE.id, name: curE.name })
        opts.push(...list.filter(e => e.id !== curE.id))
        this.entityOptions = opts.sort((a, b) => a.name.localeCompare(b.name))
      } catch (e) {
        console.error('Failed to load entities for focus capture:', e)
        this.entityOptions = []
      }
    },

    async save() {
      if (!this.form.title.trim() || this.saving) return
      this.saving = true
      this.error = null
      try {
        // 1. create the Focus Item
        const { data: item } = await this.$axios.post('/api/requirements/', {
          requirement_type: FOCUS_ITEM_TYPE_ID,
          title: this.form.title.trim(),
          description: this.form.description.trim(),
          priority: this.form.priority,
          entity: this.form.entity,
        })
        // 2. attach the screenshot (non-fatal if it fails — the item still exists)
        if (this.shotBlob && item?.id) {
          try {
            await uploadFocusScreenshot(item.id, this.shotBlob)
          } catch (e) {
            console.error('Screenshot upload failed (item still created):', e)
          }
        }
        this.$emit('created', item)
        this.closePanel()
      } catch (e) {
        console.error('Failed to create focus item:', e)
        this.error = 'Could not create the focus item.'
      } finally {
        this.saving = false
      }
    },

    resetForm() {
      this.form = { title: '', description: '', priority: 'p1', entity: null }
      this.setBlob(null)
      this.error = null
    },

    closePanel() {
      this.panelOpen = false
      this.resetForm()
    },
  },
}
</script>

<style scoped>
.fic-root { position: fixed; z-index: 2147483000; }

.fic-select-layer {
  position: fixed;
  inset: 0;
  z-index: 2147483001;
  cursor: crosshair;
  background: rgba(17, 24, 39, 0.35);
}
.fic-select-hint {
  position: fixed;
  top: 18px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(17, 24, 39, 0.9);
  color: #fff;
  font-size: 12px;
  padding: 7px 14px;
  border-radius: 6px;
  pointer-events: none;
  white-space: nowrap;
}
.fic-select-rect {
  position: fixed;
  border: 2px solid #D97706;
  background: rgba(217, 119, 6, 0.12);
  box-shadow: 0 0 0 9999px rgba(17, 24, 39, 0.15);
  pointer-events: none;
}

.fic-fab {
  position: fixed;
  bottom: 22px;
  right: 22px;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  border: none;
  background: #D97706;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(217, 119, 6, 0.4);
  transition: transform 0.15s, box-shadow 0.15s;
}
.fic-fab:hover { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(217, 119, 6, 0.5); }

/* Centered modal + backdrop */
.fic-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2147483002;
  background: rgba(17, 24, 39, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.fic-modal {
  width: 760px;
  max-width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.fic-panel-head { display: flex; align-items: center; justify-content: space-between; }
.fic-panel-title { font-size: 17px; font-weight: 600; color: #1F2937; }
.fic-x { border: none; background: transparent; color: #9CA3AF; cursor: pointer; padding: 2px; }
.fic-x:hover { color: #4B5563; }

/* Two columns: screenshot | form */
.fic-body { display: flex; gap: 20px; align-items: flex-start; }
.fic-shot-col { width: 320px; flex-shrink: 0; display: flex; flex-direction: column; gap: 8px; }
.fic-form-col { flex: 1; display: flex; flex-direction: column; gap: 6px; min-width: 0; }

.fic-shot {
  position: relative;
  border: 1px solid #F2EEE8;
  border-radius: 8px;
  background: #FAFAF8;
  min-height: 200px;
  max-height: 420px;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
}
.fic-shot-img { width: 100%; height: auto; display: block; }
.fic-shot-loading, .fic-shot-empty { font-size: 12px; color: #A9A29A; padding: 60px 0; }
.fic-shot-loading { display: flex; flex-direction: column; align-items: center; gap: 12px; }
.fic-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid #E5E7EB;
  border-top-color: #D97706;
  border-radius: 50%;
  animation: fic-spin 0.7s linear infinite;
}
@keyframes fic-spin { to { transform: rotate(360deg); } }
.fic-recapture {
  align-self: flex-start;
  font-size: 11px;
  padding: 5px 10px;
  background: #fff;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  color: #4B5563;
  cursor: pointer;
}
.fic-recapture:hover { background: #F9FAFB; }

.fic-label {
  font-size: 11px;
  font-weight: 600;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-top: 6px;
}
.fic-input, .fic-textarea {
  width: 100%;
  padding: 9px 11px;
  border: 1px solid #E5E7EB;
  border-radius: 7px;
  font-size: 14px;
  color: #1F2937;
  outline: none;
  box-sizing: border-box;
}
.fic-textarea { resize: vertical; line-height: 1.6; min-height: 120px; }
.fic-input:focus, .fic-textarea:focus { border-color: #D97706; }

.fic-row { display: flex; gap: 12px; }
.fic-field { display: flex; flex-direction: column; gap: 6px; }
.fic-field--grow { flex: 1; min-width: 0; }
.fic-select {
  padding: 8px 10px;
  border: 1px solid #E5E7EB;
  border-radius: 7px;
  font-size: 13px;
  color: #374151;
  background: #fff;
  width: 100%;
  box-sizing: border-box;
}

.fic-error { font-size: 12px; color: #DC2626; }

.fic-actions { display: flex; gap: 10px; justify-content: flex-end; }
.fic-btn-ghost {
  padding: 9px 16px;
  font-size: 13px;
  color: #6B7280;
  background: #fff;
  border: 1px solid #D1D5DB;
  border-radius: 7px;
  cursor: pointer;
}
.fic-btn-ghost:hover { background: #F9FAFB; }
.fic-btn-primary {
  padding: 9px 18px;
  font-size: 13px;
  font-weight: 500;
  color: #fff;
  background: #D97706;
  border: none;
  border-radius: 7px;
  cursor: pointer;
}
.fic-btn-primary:hover:not(:disabled) { background: #C26905; }
.fic-btn-primary:disabled { opacity: 0.5; cursor: default; }
</style>
